//#region Imports

import { Component, effect, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { MenuComponent } from './shared/components/menu/menu.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { filter, Subscription } from 'rxjs';
import { Menu } from './shared/components/menu/models/menu';
import { PermissionService } from './shared/services/permission/permission.service';
import { PermissionEnum, ResourceEnum } from '../environments/shared-environment/permission';
import { PageKey } from './shared/services/page/models/page.key';
import { ProgressComponent } from './shared/components/export/progress.component';
import { ThemeService, THEME_DARK_MODE_SELECTOR } from './shared/services/theme/theme.service';
import { MeService } from './shared/services/me/me.service';
import { ToastModule } from 'primeng/toast';
import { GlobalSpinnerService } from './shared/services/global-spinner/global-spinner.service';
import { ParamsService } from './shared/services/params/params.service';
import { ButtonModule } from 'primeng/button';
import { ToastComponent } from './shared/components/toast/toast.component';
import { GlobalCacheConfig } from 'ts-cacheable';
import { PrimeNG } from 'primeng/config';
import { Liga } from '../themes/presets/liga';

//#endregion

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, LayoutComponent, MenuComponent, HeaderComponent, ProgressComponent, ToastModule, ButtonModule, ToastComponent, ImpersonationBannerComponent],
    template: `
    <div class="flex h-screen w-full flex-col overflow-hidden">
      <l-impersonation-banner />
      <l-layout class="neon-background block min-h-0 flex-1" [fullscreen]="fullscreen">
        <l-header class="block w-full h-auto" header />
        <l-menu menu [menus]="menus" />

        <router-outlet />
      </l-layout>
    </div>

    <l-toast />
    <l-progress />
  `
})
export class AppComponent implements OnInit, OnDestroy {

  //#region Constructor

  constructor(
    router: Router,
    params: ParamsService,
    route: ActivatedRoute,
    themeService: ThemeService,
    globalSpinnerService: GlobalSpinnerService,
    private readonly primeng: PrimeNG,
    private readonly meService: MeService,
    private readonly permissionService: PermissionService,
  ) {
    params.initialize();
    themeService.initialize();
    globalSpinnerService.initialize();

    GlobalCacheConfig.maxAge = 3600000;
    GlobalCacheConfig.maxCacheCount = 100;

    this.primeng.theme.set({
      preset: Liga,
      options: {
        darkModeSelector: THEME_DARK_MODE_SELECTOR,
        cssLayer: {
          name: 'primeng',
          order: 'reset, base, primeng, utilities'
        },
      }
    });

    this.routerSubscription = router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        let currentRoute = route.root;

        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }

        this.fullscreen = currentRoute.snapshot.data['fullscreen'] || false;
      });

    effect(() => {
      meService.me();
      this.checkMenu();
    });
  }

  //#endregion

  //#region Public Properties

  public fullscreen: boolean = false;

  public menus: Menu[] = [];

  //#endregion

  //#region Private Properties

  private readonly routerSubscription?: Subscription;

  //#endregion

  //#region Lifecycle Hooks

  public async ngOnInit(): Promise<void> {
    await this.meService.storageMe();
  }

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  //#endregion

  //#region Private Methods

  private checkMenu(): void {
    const can = (resource: ResourceEnum, permission: PermissionEnum, menu: Menu | Menu[]): Menu[] => {
      if (!this.permissionService.has(resource, permission)) {
        return [];
      }

      return Array.isArray(menu) ? menu : [menu];
    };

    this.menus = [
      {
        icon: 'window',
        title: 'Dashboard',
        subtitle: 'Página inicial',
        link: '/dashboard',
        key: PageKey.DASHBOARD,
      },
      ...can(ResourceEnum.USERS, PermissionEnum.VIEW, {
        icon: 'person',
        title: 'Usuários',
        link: '/users',
        key: PageKey.USERS,
      }),
      ...can(ResourceEnum.TRACKS, PermissionEnum.VIEW, {
        icon: 'map',
        title: 'Trilhas',
        subtitle: 'Trilhas',
        link: '/tracks',
        key: PageKey.TRACKS,
      }),
      ...can(ResourceEnum.CATEGORIES, PermissionEnum.VIEW, {
        icon: 'category',
        title: 'Categorias',
        subtitle: 'Categorias de cursos',
        link: '/categories',
        key: PageKey.CATEGORIES,
      }),
      ...can(ResourceEnum.TAGS, PermissionEnum.VIEW, {
        icon: 'sell',
        title: 'Tags',
        subtitle: 'Tags de pesquisa dos cursos',
        link: '/tags',
        key: PageKey.TAGS,
      }),
      ...can(ResourceEnum.COURSES, PermissionEnum.VIEW, {
        icon: 'school',
        title: 'Cursos',
        link: 'courses',
        key: PageKey.COURSES,
        submenus: [
          {
            icon: 'delete',
            title: 'Lixeira',
            link: 'courses/trash',
            key: PageKey.COURSE_TRASH,
          },
          {
            icon: 'edit',
            title: 'Rascunhos',
            link: 'courses/drafts',
            key: PageKey.COURSE_DRAFTS,
          },
        ],
      }),
    ];
  }

  //#endregion

}
